import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { toast } from 'sonner';
import EditClientDialog from '../../components/admin/EditClientDialog';
import { TrafficDot } from '../../components/admin/TrafficLights';
import ActiveSwitch from '../../components/admin/ActiveSwitch';
import { 
  Building2, 
  Search, 
  Loader2, 
  UserPlus, 
  Eye,
  CreditCard,
  DollarSign,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  Shield,
  UserCog,
  UserCheck,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Circle,
  ExternalLink,
  Pencil,
  BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminClientsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('client_id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingFacility, setEditingFacility] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientTrafficMap, setClientTrafficMap] = useState({});
  const [facilityForm, setFacilityForm] = useState({ facility_limit: '', interest_rate: '' });
  const [savingFacility, setSavingFacility] = useState(false);
  const [pageTab, setPageTab] = useState('clients');
  const [complianceData, setComplianceData] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [dataIssues, setDataIssues] = useState(null);
  const [sectorFilter, setSectorFilter] = useState('all');
  const [subSectorFilter, setSubSectorFilter] = useState('all');
  const [facets, setFacets] = useState({ sectors: [], sub_sectors: [] });
  
  const [inviteData, setInviteData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    client_id: ''
  });

  const headers = { Authorization: `Bearer ${token}` };
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sortBy, sortOrder, sectorFilter, subSectorFilter]);

  useEffect(() => { fetchFacets(); fetchClientTrafficLights(); /* eslint-disable-next-line */ }, [token]);

  // Handle URL params for deep-linking from data issues
  useEffect(() => {
    const detailId = searchParams.get('detail');
    if (detailId && clients.length > 0) {
      fetchClientDetail(detailId);
    }
  }, [searchParams, clients]);

  useEffect(() => {
    if (pageTab === 'compliance' && !complianceData && !complianceLoading) {
      fetchComplianceData();
    }
  }, [pageTab]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      if (sectorFilter !== 'all') params.append('sector', sectorFilter);
      if (subSectorFilter !== 'all') params.append('sub_sector', subSectorFilter);
      const response = await axios.get(`${API_URL}/api/admin/clients?${params}`, { headers });
      setClients(response.data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchFacets = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/clients/facets`, { headers });
      setFacets(r.data || { sectors: [], sub_sectors: [] });
    } catch {
      // non-fatal — filters just won't populate
    }
  };

  const fetchClientTrafficLights = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/clients/traffic-lights`, { headers });
      setClientTrafficMap(r.data.lights || {});
    } catch { /* non-fatal */ }
  };

  const fetchComplianceData = async () => {
    setComplianceLoading(true);
    try {
      const [compRes, issueRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/clients/compliance-overview`, { headers }),
        axios.get(`${API_URL}/api/admin/clients/data-issues`, { headers })
      ]);
      setComplianceData(compRes.data);
      setDataIssues(issueRes.data);
    } catch (err) {
      console.error('Error fetching compliance:', err);
      toast.error('Failed to load compliance data');
    } finally {
      setComplianceLoading(false);
    }
  };

  const fetchClientDetail = async (clientId) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/clients/${clientId}/detail`, { headers });
      setClientDetail(response.data);
      setDetailOpen(true);
    } catch (err) {
      console.error('Error fetching client detail:', err);
      toast.error('Failed to load client details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" /> 
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteData.email || !inviteData.first_name || !inviteData.last_name || !inviteData.client_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setInviting(true);
      await axios.post(`${API_URL}/api/admin/clients/invite`, inviteData, { headers });
      toast.success('Client invitation sent successfully');
      setInviteOpen(false);
      setInviteData({ email: '', first_name: '', last_name: '', client_id: '' });
      fetchClients();
    } catch (err) {
      console.error('Error inviting client:', err);
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/impersonate/${userId}`, {}, { headers });
      const { access_token, target_user, user } = response.data;
      const impersonatedUser = target_user || user;
      
      toast.success(`Opening session as ${impersonatedUser.first_name} ${impersonatedUser.last_name}`);
      
      // Use URL-based impersonation (same approach as investor impersonation)
      const impersonationUrl = `${window.location.origin}/client?impersonate=${access_token}`;
      window.open(impersonationUrl, '_blank');
    } catch (err) {
      console.error('Error impersonating:', err);
      toast.error(err.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const handleSaveFacility = async () => {
    if (!clientDetail?.profile?.client_id) return;
    setSavingFacility(true);
    try {
      const payload = {};
      if (facilityForm.facility_limit !== '') payload.facility_limit = parseFloat(facilityForm.facility_limit);
      if (facilityForm.interest_rate !== '') payload.interest_rate = parseFloat(facilityForm.interest_rate) / 100;
      await axios.put(`${API_URL}/api/admin/clients/${clientDetail.profile.client_id}`, payload, { headers });
      toast.success('Facility details updated');
      setEditingFacility(false);
      fetchClientDetail(clientDetail.profile.client_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingFacility(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredClients = clients.filter(client =>
    client.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.client_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unlinkedClients = clients.filter(c => !c.user_id);

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Client Management</h1>
          <p className="text-[#6C757D]">Manage borrower/client accounts</p>
        </div>
        <Button
          className="bg-amber-500 hover:bg-amber-600 text-black"
          onClick={() => setInviteOpen(true)}
          data-testid="invite-client-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Client
        </Button>
      </div>

      <Tabs value={pageTab} onValueChange={setPageTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-xs">
          <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">
            Compliance
            {dataIssues && dataIssues.total_issues > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full">{dataIssues.total_issues}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-6 mt-4">

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Total Clients</p>
                <p className="text-2xl font-bold text-[#212529]">{clients.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">With Portal Access</p>
                <p className="text-2xl font-bold text-[#212529]">
                  {clients.filter(c => c.user_id).length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Total Outstanding</p>
                <p className="text-2xl font-bold text-[#212529]">
                  {formatCurrency(clients.reduce((sum, c) => sum + (c.total_outstanding || 0), 0))}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by company name or client ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="client-search"
              />
            </div>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="w-56" data-testid="client-sector-filter">
                <SelectValue placeholder="Sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sectors</SelectItem>
                {facets.sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={subSectorFilter} onValueChange={setSubSectorFilter}>
              <SelectTrigger className="w-56" data-testid="client-sub-sector-filter">
                <SelectValue placeholder="Sub-sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sub-sectors</SelectItem>
                {facets.sub_sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {(sectorFilter !== 'all' || subSectorFilter !== 'all') && (
              <button
                onClick={() => { setSectorFilter('all'); setSubSectorFilter('all'); }}
                className="text-sm text-emerald-700 hover:underline"
                data-testid="client-clear-filters"
              >Clear filters</button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Clients Table with Sorting */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12 text-[#6C757D]">
              {clients.length === 0 
                ? 'No clients found. Import loan data to create client profiles.'
                : 'No clients match your search.'
              }
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('client_id')}
                    >
                      <span className="flex items-center">
                        Client ID {getSortIcon('client_id')}
                      </span>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('company_name')}
                    >
                      <span className="flex items-center">
                        Company Name {getSortIcon('company_name')}
                      </span>
                    </TableHead>
                    <TableHead title="Inactive = no new deals or repayments offered; history kept">Book</TableHead>
                    <TableHead>Portal Access</TableHead>
                    <TableHead 
                      className="text-center cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('active_loans')}
                    >
                      <span className="flex items-center justify-center">
                        Active Loans {getSortIcon('active_loans')}
                      </span>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('total_outstanding')}
                    >
                      <span className="flex items-center justify-end">
                        Outstanding {getSortIcon('total_outstanding')}
                      </span>
                    </TableHead>
                    <TableHead className="text-center w-16" title="Latest management accounts freshness">Mgmt A/Cs</TableHead>
                    <TableHead className="text-center w-14" title="KYC freshness">KYC</TableHead>
                    <TableHead className="text-center w-14" title="Annual Financial Statements freshness">AFS</TableHead>
                    <TableHead className="text-center w-16" title="Bank statements freshness">Bank stmt</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id || client.client_id}>
                      <TableCell className="font-mono font-medium">{client.client_id}</TableCell>
                      <TableCell>{client.company_name}</TableCell>
                      <TableCell>
                        <ActiveSwitch kind="client" id={client.client_id} active={client.is_active !== false}
                          label={client.client_id} onChanged={() => fetchClients()} testId={`client-active-switch-${client.client_id}`} />
                      </TableCell>
                      <TableCell>
                        <Badge className={client.user_id ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {client.user_id ? 'Linked' : 'Not Linked'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{client.active_loans || 0}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(client.total_outstanding)}</TableCell>
                      <TableCell className="text-center">
                        <TrafficDot light={clientTrafficMap[client.client_id]?.management_accounts} label="Management A/Cs" testId={`client-mgmt-${client.client_id}`} />
                      </TableCell>
                      <TableCell className="text-center">
                        <TrafficDot light={clientTrafficMap[client.client_id]?.kyc} label="KYC" testId={`client-kyc-${client.client_id}`} />
                      </TableCell>
                      <TableCell className="text-center">
                        <TrafficDot light={clientTrafficMap[client.client_id]?.afs} label="AFS" testId={`client-afs-${client.client_id}`} />
                      </TableCell>
                      <TableCell className="text-center">
                        <TrafficDot light={clientTrafficMap[client.client_id]?.bank_statements} label="Bank statements" testId={`client-bank-${client.client_id}`} />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchClientDetail(client.client_id)}
                            data-testid={`view-client-${client.client_id}`}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingClient(client)}
                            data-testid={`edit-client-${client.client_id}`}
                            title="Edit client"
                            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/clients/${client.client_id}/loan-ledger`)}
                            data-testid={`loan-ledger-${client.client_id}`}
                            title="Per-loan-number ledger"
                            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          >
                            <BookOpen className="h-4 w-4" />
                          </Button>
                          {client.user_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleImpersonate(client.user_id)}
                              data-testid={`impersonate-${client.client_id}`}
                              title="View as Client"
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            >
                              <UserCheck className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="compliance" className="space-y-6 mt-4">
          {complianceLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : complianceData ? (
            <>
              {/* Compliance Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Clients</p>
                    <p className="text-2xl font-bold text-[#212529]">{complianceData.summary.total_clients}</p>
                  </CardContent>
                </Card>
                <Card className="border-green-200">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fully Compliant</p>
                    <p className="text-2xl font-bold text-green-700">{complianceData.summary.fully_compliant}</p>
                  </CardContent>
                </Card>
                <Card className={complianceData.summary.with_data_issues > 0 ? 'border-red-200' : ''}>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">With Data Issues</p>
                    <p className={`text-2xl font-bold ${complianceData.summary.with_data_issues > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {complianceData.summary.with_data_issues}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Avg Compliance</p>
                    <p className="text-2xl font-bold text-[#0D3B66]">{complianceData.summary.avg_compliance_pct}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Data Issues Section */}
              {dataIssues && dataIssues.issues.length > 0 && (
                <Card className="border-red-200 bg-red-50/30" data-testid="data-issues-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-5 w-5" />
                      Data Issues ({dataIssues.total_issues})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {dataIssues.issues.map((issue, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100 hover:border-red-300 cursor-pointer transition-colors"
                          onClick={() => {
                            if (issue.link) navigate(issue.link);
                          }}
                          data-testid={`data-issue-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`inline-block w-2 h-2 rounded-full ${issue.severity === 'high' ? 'bg-red-500' : 'bg-amber-400'}`} />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{issue.message}</p>
                              <p className="text-xs text-gray-500">{issue.entity_type}: {issue.entity_id}</p>
                            </div>
                          </div>
                          <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Per-Client Compliance Table */}
              <Card data-testid="compliance-table-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Client Compliance Status</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Client</TableHead>
                          <TableHead className="text-center">KYC/FICA</TableHead>
                          <TableHead className="text-center">Monthly Docs</TableHead>
                          {complianceData.summary.months_tracked?.slice(-6).map(m => (
                            <TableHead key={m} className="text-center text-[10px] px-1 min-w-[60px]">{m}</TableHead>
                          ))}
                          <TableHead className="text-center">Issues</TableHead>
                          <TableHead className="text-center">Overall</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {complianceData.clients.map((client) => (
                          <TableRow key={client.client_id} className="hover:bg-gray-50">
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{client.company_name}</p>
                                <p className="text-xs text-gray-500 font-mono">{client.client_id}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <Badge className={
                                  client.kyc.status === 'complete' ? 'bg-green-100 text-green-800' :
                                  client.kyc.status === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                                  'bg-gray-100 text-gray-600'
                                }>
                                  {client.kyc.done}/{client.kyc.total}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={
                                client.monthly_docs.pct === 100 ? 'bg-green-100 text-green-800' :
                                client.monthly_docs.pct > 0 ? 'bg-amber-100 text-amber-800' :
                                'bg-gray-100 text-gray-600'
                              }>
                                {client.monthly_docs.pct}%
                              </Badge>
                            </TableCell>
                            {client.monthly_docs.months.slice(-6).map((m) => (
                              <TableCell key={`${m.year}-${m.month}`} className="text-center px-1">
                                <div className="flex flex-col items-center gap-0.5">
                                  {m.management_accounts ? (
                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                  ) : (
                                    <Circle className="h-3 w-3 text-gray-300" />
                                  )}
                                  {m.bank_statements ? (
                                    <CheckCircle2 className="h-3 w-3 text-blue-500" />
                                  ) : (
                                    <Circle className="h-3 w-3 text-gray-300" />
                                  )}
                                </div>
                              </TableCell>
                            ))}
                            <TableCell className="text-center">
                              {client.data_issue_count > 0 ? (
                                <Badge className="bg-red-100 text-red-800 cursor-pointer" onClick={() => {
                                  if (client.data_issues[0]?.link) navigate(client.data_issues[0].link);
                                }}>
                                  {client.data_issue_count}
                                </Badge>
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      client.overall_pct >= 80 ? 'bg-green-500' :
                                      client.overall_pct >= 40 ? 'bg-amber-400' : 'bg-red-400'
                                    }`}
                                    style={{ width: `${client.overall_pct}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-gray-600">{client.overall_pct}%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Legend */}
                  <div className="flex items-center gap-4 px-4 py-3 border-t text-xs text-gray-500">
                    <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-500" /> Mgmt Accounts</span>
                    <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-blue-500" /> Bank Statements</span>
                    <span className="flex items-center gap-1"><Circle className="h-3 w-3 text-gray-300" /> Not uploaded</span>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <ShieldCheck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Click the Compliance tab to load compliance data</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invite Client Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Client to Portal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="client_id">Select Client *</Label>
              <Select value={inviteData.client_id} onValueChange={(v) => setInviteData(prev => ({ ...prev, client_id: v }))}>
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {unlinkedClients.length === 0 ? (
                    <div className="p-2 text-center text-sm text-gray-500">
                      All clients have portal access
                    </div>
                  ) : (
                    unlinkedClients.map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.client_id} - {client.company_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={inviteData.first_name}
                  onChange={(e) => setInviteData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={inviteData.last_name}
                  onChange={(e) => setInviteData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviting || unlinkedClients.length === 0} className="bg-amber-500 hover:bg-amber-600 text-black">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enhanced Client Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {clientDetail?.profile?.company_name || 'Client Details'}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : clientDetail && (
            <Tabs defaultValue={searchParams.get('tab') || "overview"} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="loans">Loans</TabsTrigger>
                <TabsTrigger value="kyc">KYC</TabsTrigger>
                <TabsTrigger value="submissions">Monthly Docs</TabsTrigger>
              </TabsList>
              
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-[#6C757D]">Client ID</p>
                      <p className="text-lg font-mono font-bold">{clientDetail.profile?.client_id}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-[#6C757D]">Active Loans</p>
                      <p className="text-lg font-bold text-green-600">{clientDetail.statistics?.active_loans_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-[#6C757D]">Total Outstanding</p>
                      <p className="text-lg font-bold text-red-600">{formatCurrency(clientDetail.statistics?.total_outstanding)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-[#6C757D]">Avg Interest Rate</p>
                      <p className="text-lg font-bold">{((clientDetail.statistics?.average_interest_rate || 0) * 100).toFixed(2)}%</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-[#6C757D]">Facility Limit</p>
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(clientDetail.profile?.facility_limit || clientDetail.profile?.credit_limit || 0)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-[#6C757D]">Default Interest Rate</p>
                      <p className="text-lg font-bold">{clientDetail.profile?.interest_rate != null ? `${(clientDetail.profile.interest_rate * 100).toFixed(2)}%` : 'Not set'}</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Editable Facility & Rate */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Facility & Rate Settings</CardTitle>
                      {!editingFacility ? (
                        <Button size="sm" variant="outline" onClick={() => {
                          setFacilityForm({
                            facility_limit: clientDetail.profile?.facility_limit || clientDetail.profile?.credit_limit || '',
                            interest_rate: clientDetail.profile?.interest_rate != null ? (clientDetail.profile.interest_rate * 100).toFixed(2) : ''
                          });
                          setEditingFacility(true);
                        }} data-testid="edit-facility-btn">
                          Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingFacility(false)}>Cancel</Button>
                          <Button size="sm" className="bg-[#1D2D2A]" onClick={handleSaveFacility} disabled={savingFacility} data-testid="save-facility-btn">
                            {savingFacility ? 'Saving...' : 'Save'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editingFacility ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-sm">Facility Limit (ZAR)</Label>
                          <Input
                            type="number"
                            value={facilityForm.facility_limit}
                            onChange={(e) => setFacilityForm(prev => ({ ...prev, facility_limit: e.target.value }))}
                            placeholder="e.g. 5000000"
                            data-testid="input-facility-limit"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Default Interest Rate (%)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={facilityForm.interest_rate}
                            onChange={(e) => setFacilityForm(prev => ({ ...prev, interest_rate: e.target.value }))}
                            placeholder="e.g. 12.00"
                            data-testid="input-interest-rate"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-[#6C757D]">Facility Limit</p>
                          <p className="font-medium">{formatCurrency(clientDetail.profile?.facility_limit || clientDetail.profile?.credit_limit || 0)}</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Default Interest Rate</p>
                          <p className="font-medium">{clientDetail.profile?.interest_rate != null ? `${(clientDetail.profile.interest_rate * 100).toFixed(2)}% p.a.` : 'Not set'}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* User Account Info */}
                {clientDetail.user ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <UserCog className="h-4 w-4" />
                        Portal Account
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-[#6C757D]">Name</p>
                          <p className="font-medium">{clientDetail.user.first_name} {clientDetail.user.last_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-[#6C757D]">Email</p>
                          <p className="font-medium">{clientDetail.user.email}</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleImpersonate(clientDetail.user.id)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View as Client
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="pt-6 text-center text-[#6C757D]">
                      <p>No portal account linked</p>
                      <Button 
                        size="sm" 
                        className="mt-2 bg-amber-500 hover:bg-amber-600 text-black"
                        onClick={() => {
                          setDetailOpen(false);
                          setInviteData(prev => ({ ...prev, client_id: clientDetail.profile?.client_id }));
                          setInviteOpen(true);
                        }}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Invite to Portal
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Loans Tab */}
              <TabsContent value="loans" className="space-y-4 pt-4">
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-green-600" />
                    Active Loans ({clientDetail.statistics?.active_loans_count || 0})
                  </h4>
                  {clientDetail.active_loans?.length > 0 ? (
                    <div className="max-h-60 overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Loan ID</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="text-right">Outstanding</TableHead>
                            <TableHead className="text-center">Rate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientDetail.active_loans.slice(0, 20).map(loan => (
                            <TableRow key={loan.loan_id}>
                              <TableCell className="font-mono text-sm">{loan.loan_id}</TableCell>
                              <TableCell>{loan.deal_type}</TableCell>
                              <TableCell className="text-right">{formatCurrency(loan.loan_amount_zar)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(loan.closing_balance)}</TableCell>
                              <TableCell className="text-center">{((loan.interest_rate || 0) * 100).toFixed(1)}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-[#6C757D] text-sm">No active loans</p>
                  )}
                  
                  <h4 className="font-semibold flex items-center gap-2 pt-4">
                    <FileText className="h-4 w-4 text-gray-500" />
                    Closed Loans ({clientDetail.statistics?.closed_loans_count || 0})
                  </h4>
                  {clientDetail.closed_loans?.length > 0 ? (
                    <div className="max-h-40 overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Loan ID</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Closing Balance</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientDetail.closed_loans.slice(0, 10).map(loan => (
                            <TableRow key={loan.loan_id}>
                              <TableCell className="font-mono text-sm">{loan.loan_id}</TableCell>
                              <TableCell>{loan.deal_type}</TableCell>
                              <TableCell className="text-right">{formatCurrency(loan.closing_balance)}</TableCell>
                              <TableCell>
                                <Badge variant="secondary">{loan.status}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-[#6C757D] text-sm">No closed loans</p>
                  )}
                </div>
              </TabsContent>

              {/* KYC Tab */}
              <TabsContent value="kyc" className="space-y-4 pt-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  KYC Documents
                </h4>
                {clientDetail.kyc_documents?.length > 0 ? (
                  <div className="space-y-2">
                    {clientDetail.kyc_documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{doc.document_type?.replace('_', ' ').toUpperCase()}</p>
                          <p className="text-sm text-[#6C757D]">{doc.file_name || 'No file'}</p>
                        </div>
                        <Badge className={
                          doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                          doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-amber-100 text-amber-800'
                        }>
                          {doc.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#6C757D] text-center py-8">No KYC documents submitted</p>
                )}
              </TabsContent>

              {/* Monthly Submissions Tab */}
              <TabsContent value="submissions" className="space-y-4 pt-4">
                <h4 className="font-semibold">Monthly Document Submissions</h4>
                {clientDetail.monthly_submissions?.length > 0 ? (
                  <div className="space-y-2">
                    {clientDetail.monthly_submissions.map(sub => (
                      <div key={sub.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">
                            {new Date(sub.period_year, sub.period_month - 1).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                          </p>
                          <div className="flex gap-2 text-sm">
                            <span className={sub.management_accounts_uploaded ? 'text-green-600' : 'text-gray-400'}>
                              Management Accounts: {sub.management_accounts_uploaded ? '✓' : '✗'}
                            </span>
                            <span className={sub.bank_statements_uploaded ? 'text-green-600' : 'text-gray-400'}>
                              Bank Statements: {sub.bank_statements_uploaded ? '✓' : '✗'}
                            </span>
                          </div>
                        </div>
                        <Badge className={
                          sub.status === 'complete' ? 'bg-green-100 text-green-800' :
                          sub.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-600'
                        }>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#6C757D] text-center py-8">No monthly submissions yet</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <EditClientDialog
        open={!!editingClient}
        onOpenChange={(v) => !v && setEditingClient(null)}
        client={editingClient}
        onSaved={() => { setEditingClient(null); fetchClients(); fetchClientTrafficLights(); }}
      />
    </div>
  );
};

export default AdminClientsPage;
